import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLoginPage = () => {
  const [isSetup, setIsSetup] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    phone: ""
  });
  
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      let response;
      if (isSetup) {
        response = await axios.post(`${API}/admin/setup`, formData);
        toast.success("Admin account created!");
      } else {
        response = await axios.post(`${API}/admin/login`, {
          email: formData.email,
          password: formData.password
        });
        toast.success("Welcome back!");
      }
      
      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_user", JSON.stringify(response.data.admin));
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Auth error:", error);
      if (error.response?.status === 400 && error.response?.data?.detail === "Admin already exists") {
        setIsSetup(false);
        toast.error("Admin already exists. Please login.");
      } else {
        toast.error(error.response?.data?.detail || "Authentication failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-10 overflow-hidden">
        <div className="absolute top-20 left-20 w-96 h-96 bg-[#10B981] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-[#10B981] mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Site
        </Link>
        
        <div className="bg-slate-900 rounded-2xl p-8 border border-slate-800">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#10B981]/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              {isSetup ? "Create Admin Account" : "Admin Portal"}
            </h1>
            <p className="text-slate-400 text-sm">
              {isSetup ? "Set up your admin credentials" : "Sign in to manage SolveDebts"}
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            {isSetup && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Full Name</label>
                  <Input
                    type="text"
                    placeholder="Enter your name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    required={isSetup}
                    className="h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Phone</label>
                  <Input
                    type="tel"
                    placeholder="Enter your phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required={isSetup}
                    className="h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>
              </>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email</label>
              <Input
                type="email"
                placeholder="admin@solvedebts.co.uk"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="admin-email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="h-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 pr-12"
                  data-testid="admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#10B981] hover:bg-[#059669] text-white font-semibold"
              data-testid="admin-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  {isSetup ? "Creating..." : "Signing in..."}
                </span>
              ) : (
                isSetup ? "Create Admin Account" : "Sign In"
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <button
              onClick={() => setIsSetup(!isSetup)}
              className="text-sm text-slate-400 hover:text-[#10B981] transition-colors"
            >
              {isSetup ? "Already have an account? Sign in" : "First time? Create admin account"}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
